import React, { useState, useEffect } from 'react';
import { X, Lock, CheckCircle2, AlertCircle, ArrowRightLeft } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { vendorCodeConflictService, getErrorMessage } from '../../../services/apiService';

/**
 * Give a vendor row one or more ERP codes of its own.
 *
 * The same company registered in several states holds a separate ERP code per GST
 * registration, so a "duplicate" row is frequently a second legitimate registration
 * rather than junk. This lists the live ERP codes for the company and greys out the ones
 * another row already holds, leaving the free ones selectable.
 */
const AssignErpCodeModal = ({ vendor, onClose, onAssigned }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await vendorCodeConflictService.getErpCandidates(vendor.id);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vendor.id]);

  const toggle = (code) =>
    setSelected((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));

  const submit = async () => {
    setSaving(true);
    try {
      const res = await vendorCodeConflictService.assignErpCodes(vendor.id, selected);
      onAssigned(res.data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const candidates = data?.candidates || [];
  // Free, or held by a sibling row of the same company (which we can move it off).
  const selectable = candidates.filter((c) => !c.taken || c.movable);
  const movingCount = selected.filter((code) => candidates.find((c) => c.code === code)?.movable).length;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Assign ERP code</h2>
            <p className="text-sm text-gray-500 truncate">
              {vendor.company_name} <span className="text-gray-400">#{vendor.id}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {loading ? (
            <p className="text-center py-10 text-gray-500">Looking this company up in the ERP…</p>
          ) : error ? (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-center py-10 text-gray-500">
              The ERP has no vendor code under this company name.
            </p>
          ) : (
            <>
              {data.vendor.codes.length > 0 && (
                <p className="text-sm text-gray-600 mb-3">
                  Already holds{' '}
                  <span className="font-mono font-semibold">{data.vendor.codes.join(', ')}</span>
                  {' — '}selecting more adds to it.
                </p>
              )}
              {selectable.length === 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-900">
                    Every ERP code for this company is held by a different company's record, so none
                    can be assigned here. This row has to be merged or removed instead.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {candidates.map((c) => {
                  const isSelected = selected.includes(c.code);
                  const locked = c.taken && !c.movable;
                  return (
                    <label
                      key={c.code}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition ${
                        locked
                          ? 'bg-slate-50 border-slate-200 cursor-not-allowed opacity-70'
                          : isSelected
                            ? 'border-amber-500 ring-2 ring-amber-200 cursor-pointer'
                            : 'hover:border-gray-400 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        disabled={locked}
                        checked={isSelected}
                        onChange={() => toggle(c.code)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-gray-900">{c.code}</span>
                          {c.state && (
                            <span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                              {c.state}
                            </span>
                          )}
                          {c.dataAreaId && (
                            <span className="text-xs text-gray-400">{c.dataAreaId}</span>
                          )}
                          {c.movable && (
                            <span className="text-xs bg-blue-100 text-blue-800 rounded-full px-2 py-0.5">
                              moves here
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 break-all mt-0.5">{c.name}</p>
                        {c.email && <p className="text-xs text-gray-400 break-all">{c.email}</p>}
                        {c.movable ? (
                          <p className="flex items-start gap-1 text-xs text-blue-700 mt-1">
                            <ArrowRightLeft className="h-3 w-3 shrink-0 mt-0.5" />
                            <span>
                              Currently on {c.taken_by} — the same company. Selecting it moves the
                              code onto this row.
                            </span>
                          </p>
                        ) : c.taken ? (
                          <p className="flex items-center gap-1 text-xs text-slate-600 mt-1">
                            <Lock className="h-3 w-3 shrink-0" />
                            Held by {c.taken_by} — a different company
                          </p>
                        ) : null}
                      </div>
                    </label>
                  );
                })}
              </div>

              {movingCount > 0 && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <ArrowRightLeft className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-blue-900">
                    {movingCount} code{movingCount > 1 ? 's' : ''} will be moved off another row of
                    this company. That row keeps its remaining codes, or becomes code-less if it had
                    only this one. Both records are audited.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={selected.length === 0 || saving} loading={saving}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Assign {selected.length || ''}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignErpCodeModal;
